/**
 * Lobby socket, edit sync, and the play bridge. Guests propose full documents; the host three-way
 * merges each onto the current level (see merge.ts) and commits it, so edits to different things never
 * undo each other. Only real overlaps, or edits to something another member has locked, are refused.
 * Only the host writes the level and simulates play.
 */
import { useSyncExternalStore } from 'react';
import type { LevelDoc } from '../model/format.ts';
import { toNetInput, type NetPlay } from '../play/net.ts';
import { saveNow } from '../editor/autosave.ts';
import { isLive, onActiveInput, playFrom, rebaseLive, revertLive, useEditor, withoutDerived, type Selection } from '../editor/store.ts';
import { mergeLevels, same } from './merge.ts';
import { applyLobbyAsset, isLobbyAssetKind } from '../data/assetSync.ts';
import { onAssetSaved } from '../data/remoteStore.ts';
import { reloadTerrainAssets } from '../terrain/index.ts';
import { useSettings } from '../editor/settings.ts';
import { cleanColor, cleanName, emptyEdges, isLevelWire, splitEdges, type ClientMsg, type LobbySummary, type NetEdges, type NetInput, type NetSnap, type RoomView, type ServerMsg } from './protocol.ts';

export interface LobbyCursor {
  accountId: string;
  x: number;
  y: number;
  sel: string[];
}

export interface LobbyState {
  status: 'off' | 'connecting' | 'live';
  lobbies: LobbySummary[];
  room: RoomView | null;
  cursors: LobbyCursor[];
  /** true once the server has started play for this room */
  playing: boolean;
}

const ERRORS: Record<string, string> = {
  not_found: 'That lobby or level is no longer available',
  not_host: 'Only the host can do that',
  not_guest: 'The host applies edits directly',
  not_while_playing: 'The level stays frozen until the host ends play',
  host_switch_while_playing: 'Switch hosts after play ends. The physics stay on the machine that started them.',
  slot_taken: 'That player slot is taken',
  locked: 'Someone else is editing that',
  full: 'That lobby is full',
  no_slot: 'All four player spots are taken',
  bad_doc: 'That edit could not be applied',
  rate: 'Slow down a little and try again',
  bad_id: 'Save the level before hosting it',
};

let state: LobbyState = { status: 'off', lobbies: [], room: null, cursors: [], playing: false };
let rev = 0;
let inflight = false;
let queued = false;
let queuedFrom: string | undefined;
let dragging = false;
let blocked = false;
let heldLocks: string[] = [];
let inputLocks: string[] = [];
let suppress = 0;
/** the last level the server sent: local edits sit on top of it */
let syncBase: LevelDoc | null = null;
/** what this client sent and has not seen come back yet, oldest first (each echo is merged against its own) */
const sent: LevelDoc[] = [];
/** host: committed levels by revision, the bases guest proposals are merged from */
const history = new Map<number, LevelDoc>();
/** host: proposals that arrived while its own commit was in flight */
const proposals: Extract<ServerMsg, { t: 'propose' }>[] = [];

function remember(r: number, doc: unknown) {
  if (!isLevelWire(doc)) return;
  history.set(r, doc as LevelDoc);
  for (const k of history.keys()) if (k < r - 40) history.delete(k);
}
let socket: WebSocket | null = null;
const outbox: ClientMsg[] = [];
let stopped = false;
let retry = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();
interface HeldGuest {
  movement: { x: number; y: number };
  control: { x: number; y: number };
  interactDown: boolean;
  controlEnabled: boolean;
  leftTrigger: number;
  rightTrigger: number;
  edges: NetEdges[];
}

const guestInputs = new Map<number, HeldGuest>();
let takePending = false;

function writeSlots(next: (string | null)[]) {
  for (let i = 0; i < 4; i++) playSlots[i] = next[i] ?? null;
  playNames.length = 0;
  const room = state.room;
  for (let i = 0; i < playSlots.length; i++) {
    const id = playSlots[i];
    if (!id) continue;
    const member = room?.members.find((item) => item.id === id);
    playNames.push(member?.email ?? 'Player');
  }
}

function latchInput(slot: number, input: NetInput) {
  let held = guestInputs.get(slot);
  if (!held) {
    held = { movement: { x: 0, y: 0 }, control: { x: 0, y: 0 }, interactDown: false, controlEnabled: false, leftTrigger: 0, rightTrigger: 0, edges: [] };
    guestInputs.set(slot, held);
  }
  held.movement = input.movement;
  held.control = input.control;
  held.interactDown = input.interactDown;
  held.controlEnabled = !!input.controlEnabled;
  held.leftTrigger = input.leftTrigger ?? 0;
  held.rightTrigger = input.rightTrigger ?? 0;
  held.edges.push(...splitEdges(input));
  if (held.edges.length > 24) held.edges.splice(0, held.edges.length - 24);
}

function pullOne(): Map<number, NetInput> {
  const out = new Map<number, NetInput>();
  for (const [slot, held] of guestInputs) {
    const edge = held.edges.shift() ?? emptyEdges();
    out.set(slot, {
      movement: held.movement,
      control: held.control,
      interactDown: held.interactDown,
      controlEnabled: held.controlEnabled,
      leftTrigger: held.leftTrigger,
      rightTrigger: held.rightTrigger,
      restartPressed: false,
      ...edge,
    });
  }
  return out;
}
let snapSeq = 0;
let latestSnap: { seq: number; snap: NetSnap } | null = null;
const menuQueue: { cmd: string; n?: number }[] = [];
const playSlots: (string | null)[] = [null, null, null, null];
const playNames: (string | null)[] = [];
let playSpawn: [number, number] | undefined;

onAssetSaved((kind, id, data) => {
  if (!state.room) return;
  if (!isLobbyAssetKind(kind)) return;
  send({ t: 'asset', kind, id, data });
});

function emit() {
  for (const fn of listeners) fn();
}

export function getLobby(): LobbyState {
  return state;
}

export function useLobby(): LobbyState {
  return useSyncExternalStore(subscribeLobby, getLobby, getLobby);
}

function subscribeLobby(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function isLobbyGuest(levelId: string | null | undefined): boolean {
  const room = state.room;
  return !!room && !!levelId && room.levelId === levelId && room.hostId !== room.self;
}

function send(msg: ClientMsg) {
  if (msg.t === 'ping' && (!socket || socket.readyState !== WebSocket.OPEN)) return;
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(JSON.stringify(msg));
    } catch {
      /* drop a frame; a throw must not kill editing */
    }
  } else outbox.push(msg);
}

function flush() {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  const pending = outbox.splice(0, outbox.length);
  for (const msg of pending) socket.send(JSON.stringify(msg));
}

function note(error: string) {
  useEditor.getState().notify(ERRORS[error] ?? 'The lobby could not do that');
}

function applyServerDoc(doc: unknown, keepView = false) {
  if (!isLevelWire(doc)) return;
  cancelDrag();
  const guest = state.room ? state.room.hostId !== state.room.self : false;
  suppress++;
  try {
    const st = useEditor.getState();
    const { selection, container } = st;
    st.setDoc(doc as LevelDoc, st.savedAt);
    if (keepView) keepViewOn(selection, container);
    if (guest) useEditor.getState().set({ saveStatus: 'lobby' });
  } finally {
    suppress--;
  }
}

/** After a level arrives: keep what was selected and the platform being edited, where they still exist. */
function keepViewOn(selection: Selection, container: string) {
  const doc = useEditor.getState().doc;
  if (!doc) return;
  const bodies = new Set(doc.bodies.map((b) => b.id));
  const terrains = new Set([...doc.terrains, ...doc.bodies.flatMap((b) => b.terrains)].map((t) => t.id));
  const objects = new Set([...doc.objects, ...doc.bodies.flatMap((b) => b.objects)].map((o) => o.id));
  let sel: Selection = selection;
  if (sel.kind === 'objects') {
    const ids = sel.ids.filter((id) => objects.has(id));
    sel = ids.length ? { kind: 'objects', ids } : { kind: 'none' };
  } else if ((sel.kind === 'body' && !bodies.has(sel.id)) || (sel.kind === 'terrain' && !terrains.has(sel.id)) || (sel.kind === 'waypoint' && !bodies.has(sel.body))) sel = { kind: 'none' };
  useEditor.setState({ selection: sel, container: container === 'level' || bodies.has(container) ? container : 'level' });
}

/** Someone's commit arrived: merge it under this client's own unsent or in-flight edits and any running drag. */
function receiveDoc(incoming: LevelDoc, mine: boolean) {
  const base = (mine && sent.shift()) || syncBase;
  syncBase = incoming;
  const st = useEditor.getState();
  if (!st.doc || !base) return applyServerDoc(incoming, true);
  const current = withoutDerived(st.doc);
  if (same(current, base)) {
    if (!isLive()) return applyServerDoc(incoming, true);
  }
  const merged = mergeLevels(base, current, incoming, 'theirs').doc;
  rebaseLive((doc) => mergeLevels(base, withoutDerived(doc), incoming, 'theirs').doc);
  suppress++;
  try {
    const { selection, container } = st;
    useEditor.setState({ doc: withoutDerived(merged), past: [], future: [], saveStatus: 'lobby' });
    keepViewOn(selection, container);
  } finally {
    suppress--;
  }
}

function cancelDrag() {
  dragging = false;
  blocked = false;
  if (heldLocks.length && state.room) send({ t: 'unlock', ids: heldLocks });
  heldLocks = [];
  revertLive();
}

function publish(from?: string, wireDoc?: LevelDoc) {
  const room = state.room;
  const doc = useEditor.getState().doc;
  if (suppress || !room || room.mode !== 'edit' || !doc || doc.id !== room.levelId) return;
  if (inflight) {
    queued = true;
    queuedFrom = from ?? queuedFrom;
    return;
  }
  inflight = true;
  queued = false;
  queuedFrom = undefined;
  const wire = wireDoc ?? withoutDerived(doc);
  sent.push(wire);
  if (sent.length > 8) sent.shift();
  if (room.hostId === room.self) send({ t: 'commit', baseRev: rev, doc: wire, from });
  else send({ t: 'propose', baseRev: rev, doc: wire });
}

function afterAck() {
  inflight = false;
  if (queued) {
    const from = queuedFrom;
    queued = false;
    queuedFrom = undefined;
    publish(from);
  }
  while (!inflight && proposals.length) onPropose(proposals.shift()!);
}

/** What a drag or inspector edit locks. Painting with nothing selected locks nothing: paint merges per cell. */
function lockIds(sel: Selection): string[] {
  if (sel.kind === 'objects' && sel.ids.length) return sel.ids.slice(0, 64);
  if (sel.kind === 'body' || sel.kind === 'terrain') return [sel.id];
  if (sel.kind === 'waypoint') return [sel.body];
  return [];
}

function canLock(ids: string[]): boolean {
  const room = state.room;
  if (!room) return true;
  if (ids.includes('__level__') && room.locks.some((lock) => lock.accountId !== room.self)) return false;
  return ids.every((id) => {
    const owner = room.locks.find((lock) => lock.id === id);
    return !owner || owner.accountId === room.self;
  });
}

function onPropose(msg: Extract<ServerMsg, { t: 'propose' }>) {
  const room = state.room;
  if (!room || room.hostId !== room.self) return;
  const refuse = () => send({ t: 'reject', accountId: msg.from });
  const st = useEditor.getState();
  if (room.mode !== 'edit' || !isLevelWire(msg.doc) || !st.doc) return refuse();
  if (inflight) {
    proposals.push(msg);
    return;
  }
  const base = history.get(msg.baseRev);
  if (!base) return refuse();
  const theirs = withoutDerived(msg.doc as LevelDoc);
  const merged = mergeLevels(base, withoutDerived(st.doc), theirs, 'mine');
  const locked = room.locks.some((lock) => lock.accountId !== msg.from && (merged.theirChanges.has(lock.id) || (lock.id === '__level__' && merged.theirChanges.size > 0)));
  if (merged.conflicts.length || locked) return refuse();
  // a drag the host is in the middle of stays local until it ends; the commit carries the guest edit only
  let wire = merged.doc;
  const live = isLive();
  if (live)
    rebaseLive((doc) => {
      wire = mergeLevels(base, withoutDerived(doc), theirs, 'mine').doc;
      return wire;
    });
  suppress++;
  try {
    useEditor.setState({
      doc: merged.doc,
      past: live ? st.past : [...st.past.slice(-199), { doc: st.doc, label: 'Guest edit' }],
      future: [],
      dirty: true,
    });
  } finally {
    suppress--;
  }
  publish(msg.from, wire);
}

function onMessage(msg: ServerMsg) {
  if (msg.t === 'lobbies') {
    state = { ...state, lobbies: msg.lobbies };
    emit();
    return;
  }
  if (msg.t === 'joined') {
    rev = msg.room.rev;
    inflight = false;
    queued = false;
    history.clear();
    proposals.length = 0;
    remember(rev, msg.doc);
    syncBase = isLevelWire(msg.doc) ? (msg.doc as LevelDoc) : null;
    sent.length = 0;
    state = { ...state, room: msg.room, cursors: [], playing: msg.room.mode === 'play' };
    applyServerDoc(msg.doc);
    sendProfile();
    const roomId = msg.room.id;
    const playing = msg.room.mode === 'play';
    if (playing) writeSlots(slotsFromMembers(msg.room.members));
    emit();
    void reloadTerrainAssets()
      .catch(() => undefined)
      .then(() => {
        if (!state.room || state.room.id !== roomId) return;
        if (playing && state.playing) playFrom();
      });
    return;
  }
  if (msg.t === 'room') {
    const wasGuest = state.room && state.room.hostId !== state.room.self;
    rev = msg.room.rev;
    state = { ...state, room: msg.room };
    if (wasGuest && msg.room.hostId === msg.room.self) useEditor.getState().set({ dirty: true, saveStatus: 'pending' });
    if (msg.room.hostId !== msg.room.self) useEditor.getState().set({ saveStatus: 'lobby' });
    emit();
    return;
  }
  if (msg.t === 'left' || msg.t === 'kicked') {
    const kicked = msg.t === 'kicked';
    clearRoom();
    if (kicked) useEditor.getState().notify('The host removed you from the lobby');
    emit();
    return;
  }
  if (msg.t === 'replaced') {
    stopped = true;
    clearRoom();
    socket?.close();
    useEditor.getState().notify('This account joined the lobby from another tab');
    emit();
    return;
  }
  if (msg.t === 'error') {
    if (msg.error === 'rate' || msg.error === 'bad_doc' || msg.error === 'not_while_playing' || msg.error === 'not_host' || msg.error === 'not_guest') {
      inflight = false;
      sent.pop();
      if (msg.error === 'rate') setTimeout(() => publish(), 120);
      else queued = false;
    }
    if (msg.error === 'no_slot' || msg.error === 'full') takePending = false;
    if (msg.error !== 'rate') note(msg.error);
    if (msg.error === 'not_found') clearRoom();
    emit();
    return;
  }
  if (msg.t === 'cursor') {
    const cursors = state.cursors.filter((c) => c.accountId !== msg.accountId);
    cursors.push({ accountId: msg.accountId, x: msg.x, y: msg.y, sel: msg.sel });
    state = { ...state, cursors };
    emit();
    return;
  }
  if (msg.t === 'propose') {
    onPropose(msg);
    return;
  }
  if (msg.t === 'doc') {
    const room = state.room;
    rev = msg.rev;
    remember(msg.rev, msg.doc);
    if (room) state = { ...state, room: { ...room, rev: msg.rev } };
    const mine = msg.from === room?.self;
    const host = room?.hostId === room?.self;
    if (host) {
      syncBase = msg.doc as LevelDoc;
      sent.length = 0;
    } else if (isLevelWire(msg.doc)) receiveDoc(msg.doc as LevelDoc, mine);
    afterAck();
    emit();
    return;
  }
  if (msg.t === 'reject') {
    rev = msg.rev;
    inflight = false;
    queued = false;
    remember(msg.rev, msg.doc);
    syncBase = isLevelWire(msg.doc) ? (msg.doc as LevelDoc) : syncBase;
    sent.length = 0;
    applyServerDoc(msg.doc, true);
    useEditor.getState().notify('That edit overlapped someone else, so it was put back');
    emit();
    return;
  }
  if (msg.t === 'play') {
    playSpawn = msg.spawn;
    guestInputs.clear();
    takePending = false;
    latestSnap = null;
    if (state.room) state = { ...state, room: { ...state.room, mode: 'play', locks: [] }, playing: true, cursors: [] };
    writeSlots(msg.slots);
    if (state.room && state.room.hostId !== state.room.self) applyServerDoc(msg.doc);
    playFrom(msg.spawn);
    emit();
    return;
  }
  if (msg.t === 'slots') {
    writeSlots(msg.slots);
    takePending = false;
    emit();
    return;
  }
  if (msg.t === 'stop') {
    writeSlots([null, null, null, null]);
    guestInputs.clear();
    takePending = false;
    latestSnap = null;
    menuQueue.length = 0;
    if (state.room) state = { ...state, room: { ...state.room, mode: 'edit' }, playing: false };
    useEditor.getState().set({ playing: null });
    if (msg.reason === 'host_left') {
      applyServerDoc(msg.doc);
      useEditor.getState().notify('The host left during play, so everyone is back in the editor');
    }
    emit();
    return;
  }
  if (msg.t === 'input') {
    latchInput(msg.slot, msg.input);
    return;
  }
  if (msg.t === 'snap') {
    snapSeq += 1;
    latestSnap = { seq: snapSeq, snap: msg.snap };
    return;
  }
  if (msg.t === 'menu') {
    menuQueue.push({ cmd: msg.cmd, n: msg.n });
    return;
  }
  if (msg.t === 'pong') return;
  if (msg.t === 'asset') {
    if (msg.from === state.room?.self) return;
    applyLobbyAsset(msg.kind, msg.id, msg.data);
  }
}

function clearRoom() {
  state = { ...state, room: null, cursors: [], playing: false };
  rev = 0;
  inflight = false;
  queued = false;
  history.clear();
  proposals.length = 0;
  syncBase = null;
  sent.length = 0;
  heldLocks = [];
  inputLocks = [];
  writeSlots([null, null, null, null]);
  guestInputs.clear();
  takePending = false;
  latestSnap = null;
  menuQueue.length = 0;
  useEditor.getState().set({ playing: null });
  const st = useEditor.getState();
  if (st.saveStatus === 'lobby') st.set({ saveStatus: st.dirty ? 'pending' : 'saved' });
}

const HEARTBEAT_MS = 20_000;
const STALE_MS = 50_000;
let heartbeat: ReturnType<typeof setInterval> | undefined;
let lastSeen = 0;

function beat(ws: WebSocket) {
  if (socket !== ws || ws.readyState !== WebSocket.OPEN) return;
  if (Date.now() - lastSeen > STALE_MS) {
    ws.close();
    return;
  }
  send({ t: 'ping' });
}

function connect() {
  if (stopped) return;
  if (socket && socket.readyState !== WebSocket.CLOSED) return;
  clearTimeout(retryTimer);
  state = { ...state, status: 'connecting' };
  emit();
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const dev = new URLSearchParams(location.search).get('dev') === '2' ? '?dev=2' : '';
  const ws = new WebSocket(`${proto}://${location.host}/api/lobby/ws${dev}`);
  socket = ws;
  ws.onopen = () => {
    if (socket !== ws) {
      ws.close();
      return;
    }
    retry = 0;
    lastSeen = Date.now();
    state = { ...state, status: 'live' };
    emit();
    flush();
    if (state.room) send({ t: 'join', lobbyId: state.room.id });
    clearInterval(heartbeat);
    heartbeat = setInterval(() => beat(ws), HEARTBEAT_MS);
  };
  ws.onmessage = (ev) => {
    if (socket !== ws) return;
    lastSeen = Date.now();
    if (typeof ev.data !== 'string') return;
    try {
      onMessage(JSON.parse(ev.data) as ServerMsg);
    } catch {
      /* ignore a malformed frame */
    }
  };
  ws.onclose = () => {
    if (socket !== ws) return;
    socket = null;
    clearInterval(heartbeat);
    if (stopped) {
      state = { ...state, status: 'off' };
      emit();
      return;
    }
    retry += 1;
    state = { ...state, status: 'connecting' };
    emit();
    retryTimer = setTimeout(connect, Math.min(8000, 400 * 2 ** Math.min(retry, 5)));
  };
}

export function startLobby(): () => void {
  stopped = false;
  connect();
  const onVisible = () => {
    if (document.visibilityState === 'visible') connect();
  };
  const onOnline = () => connect();
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    clearTimeout(retryTimer);
    clearInterval(heartbeat);
    socket?.close();
    socket = null;
  };
}

function slotsFromMembers(members: RoomView['members']): (string | null)[] {
  const next: (string | null)[] = [null, null, null, null];
  for (const member of members) {
    const list = member.slots.length ? member.slots : member.slot === null ? [] : [member.slot];
    for (const slot of list) next[slot] = member.id;
  }
  return next;
}

export function netPlayOptions(): NetPlay | undefined {
  const room = state.room;
  if (!room || !state.playing) return undefined;
  const me = room.members.find((member) => member.id === room.self);
  writeSlots(playSlots);
  return {
    role: room.hostId === room.self ? 'host' : 'guest',
    slot: me?.slot ?? null,
    self: room.self,
    slots: playSlots,
    names: playNames,
    onInput: (slot, input) => send({ t: 'input', slot, input: toNetInput(input) }),
    onSnap: (snap) => send({ t: 'snap', snap }),
    onTake: () => {
      if (takePending) return;
      takePending = true;
      send({ t: 'take' });
    },
    onMenu: (cmd, n) => send(n === undefined ? { t: 'menu', cmd } : { t: 'menu', cmd, n }),
    pullInputs: () => pullOne(),
    pullMenu: () => menuQueue.splice(0, menuQueue.length),
    pullSnap: () => latestSnap,
  };
}

export function requestPlay(spawn?: [number, number]) {
  const room = state.room;
  if (!room) {
    playFrom(spawn);
    return;
  }
  if (room.hostId !== room.self) {
    useEditor.getState().notify('The host starts and stops play for everyone in the lobby');
    return;
  }
  if (room.mode === 'play') return;
  send(spawn ? { t: 'play', spawn } : { t: 'play' });
}

export function requestStop() {
  const room = state.room;
  if (!room || room.mode !== 'play') return false;
  if (room.hostId !== room.self) {
    useEditor.getState().notify('The host ends play for everyone');
    return true;
  }
  send({ t: 'stop' });
  return false;
}

export async function createLobby() {
  const doc = useEditor.getState().doc;
  if (!doc) {
    useEditor.getState().notify('Open a level first');
    return;
  }
  const ok = await saveNow();
  if (!ok) {
    useEditor.getState().notify('Save the level to the server before hosting it');
    return;
  }
  send({ t: 'create', levelId: doc.id });
}

export async function joinLobby(id: string) {
  const st = useEditor.getState();
  if (st.doc && st.dirty && !isLobbyGuest(st.doc.id)) await saveNow();
  send({ t: 'join', lobbyId: id });
}

export function leaveLobby() {
  send({ t: 'leave' });
  clearRoom();
  emit();
}

export function claimSlot(slot: number | null) {
  send({ t: 'slot', slot });
}

export function assignSlot(accountId: string, slot: number | null) {
  send({ t: 'assign', accountId, slot });
}

export function transferHost(accountId: string) {
  send({ t: 'transfer', accountId });
}

export function kickMember(accountId: string) {
  send({ t: 'kick', accountId });
}

export function sendCursor(x: number, y: number, sel: string[]) {
  if (!state.room || state.room.mode === 'play') return;
  send({ t: 'cursor', x, y, sel });
}

/** Tell the room the cursor colour and name picked in the settings. */
function sendProfile() {
  if (!state.room) return;
  const s = useSettings.getState();
  send({ t: 'profile', color: cleanColor(s.lobbyColor), name: cleanName(s.lobbyName) });
}

/** Wrap the editor store so lobby edits go through the host. */
export function installLobbySync(): () => void {
  const api = useEditor.getState();
  const orig = { change: api.change, beginLive: api.beginLive, commitLive: api.commitLive, undo: api.undo, redo: api.redo, live: api.live };
  useEditor.setState({
    change(label, recipe) {
      if (state.room?.mode === 'play') return;
      orig.change(label, recipe);
      if (!suppress) publish();
    },
    live(recipe) {
      if (blocked || state.room?.mode === 'play') return;
      orig.live(recipe);
    },
    beginLive(label) {
      blocked = false;
      if (state.room?.mode === 'play') return;
      const ids = state.room ? lockIds(useEditor.getState().selection) : [];
      if (state.room && !canLock(ids)) {
        blocked = true;
        useEditor.getState().notify('Someone else is editing that');
        return;
      }
      orig.beginLive(label);
      dragging = !!state.room;
      heldLocks = ids;
      if (state.room && ids.length) send({ t: 'lock', ids });
    },
    commitLive() {
      if (blocked) {
        blocked = false;
        dragging = false;
        heldLocks = [];
        return;
      }
      const was = dragging;
      dragging = false;
      orig.commitLive();
      if (was && heldLocks.length) send({ t: 'unlock', ids: heldLocks });
      heldLocks = [];
      if (!suppress) publish();
    },
    undo() {
      if (state.room?.mode === 'play') return;
      orig.undo();
      if (!suppress) publish();
    },
    redo() {
      if (state.room?.mode === 'play') return;
      orig.redo();
      if (!suppress) publish();
    },
  });
  onActiveInput((on) => {
    if (inputLocks.length) send({ t: 'unlock', ids: inputLocks });
    inputLocks = [];
    if (!on || !state.room || state.room.mode === 'play') return;
    const ids = lockIds(useEditor.getState().selection);
    if (!ids.length) return;
    if (!canLock(ids)) {
      useEditor.getState().notify('Someone else is editing that');
      return;
    }
    inputLocks = ids;
    send({ t: 'lock', ids });
  });
  const unsub = useEditor.subscribe((s, prev) => {
    const room = state.room;
    if (!room || !s.doc || s.doc === prev.doc) return;
    if (s.doc.id !== room.levelId) leaveLobby();
  });
  let profileTimer: ReturnType<typeof setTimeout> | undefined;
  const unsubProfile = useSettings.subscribe((s, prev) => {
    if (s.lobbyColor === prev.lobbyColor && s.lobbyName === prev.lobbyName) return;
    clearTimeout(profileTimer);
    // the colour picker fires on every drag step
    profileTimer = setTimeout(sendProfile, 250);
  });
  return () => {
    unsub();
    unsubProfile();
    clearTimeout(profileTimer);
    onActiveInput(null);
    useEditor.setState(orig);
  };
}
